import React, { useState, useEffect } from 'react';
import { BACKEND_PORT } from '../config/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stack } from "@/components/ui/layout";
import { Text, Heading } from "@/components/ui/typography";
import { Separator } from "@/components/ui/separator";
import { Upload, FileText, Plus, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const ResumeUploadModal = ({ onCreateNew, onUpload, onClose, showStartFresh = true }) => {
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Add/remove body class when modal opens/closes to prevent background scrolling
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

const processFile = async (file) => {
  const allowedTypes = [
    'application/pdf', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'application/msword' // DOC
  ];
  if (!allowedTypes.includes(file.type)) {
    setUploadError('Please upload a PDF, DOC, or DOCX file only.');
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    setUploadError('File size must be less than 5MB.');
    return;
  }

  setIsUploading(true);
  setUploadError('');

  try {
    const formData = new FormData();
    formData.append('resume', file);

    const API_URL = '/api/parse-resume';

    const response = await fetch(API_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      let errorText;
      try {
        const errorData = await response.json();
        errorText = errorData.error || 'Unknown server error';
      } catch (jsonError) {
        errorText = await response.text();
      }
      throw new Error(`Server error ${response.status}: ${errorText}`);
    }

    // ✅ Correct: parse JSON response
    const backendResponse = await response.json();

    const resumeData = backendResponse;

    // ✅ Validate key fields - updated for new backend structure
    const info = resumeData.personal_info || {};
    if (!info.full_name || !info.email || !info.phone) {
      throw new Error("Missing full name, email, or phone from resume");
    }
    // Note: professional_summary is optional - not all resumes have it

    // Save resume data to localStorage for JD Align Resume functionality
    try {
      localStorage.setItem('resumeData', JSON.stringify(resumeData));
    } catch (error) {
      // Error saving resume data to localStorage
    }

    // ✅ Send to parent
    onUpload(resumeData);

  } catch (error) {
    setUploadError(`❌ Error: ${error.message}. Is your Python server running on port ${BACKEND_PORT}?`);
  } finally {
    setIsUploading(false);
  }
};

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create or Upload Resume</DialogTitle>
        </DialogHeader>
        
        <Stack spacing="lg" className="mt-4">
          {/* Upload Section */}
          <div>
            <div className={cn("flex items-center gap-3 mb-4")}>
              <div className={cn("w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center")}>
                <Upload className="w-5 h-5 text-primary" />
              </div>
              <div>
                <Heading level="h3" className="text-lg font-semibold">Upload Existing Resume</Heading>
              </div>
            </div>
            
            <div 
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                dragActive ? "border-primary bg-primary/5" : "border-muted hover:border-primary/50",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !isUploading && document.getElementById('file-upload-input').click()}
            >
              <input 
                type="file" 
                id="file-upload-input" 
                className="hidden" 
                onChange={handleFileChange} 
                accept=".pdf,.doc,.docx"
              />
              {isUploading ? (
                <Stack spacing="sm" align="center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <Text className="font-medium">Processing your resume...</Text>
                  <Text variant="muted" className="text-sm">This may take a moment</Text>
                </Stack>
              ) : (
                <Stack spacing="sm" align="center">
                  <Upload className="w-10 h-10 text-muted-foreground" />
                  <Text className="font-medium">
                    <span className="text-primary">Click to upload</span> or drag and drop
                  </Text>
                  <Text variant="muted" className="text-xs">PDF, DOC, or DOCX (max 5MB)</Text>
                </Stack>
              )}
            </div>
            
            {uploadError && (
              <div className={cn("mt-3 p-3 rounded-md bg-destructive/10 border border-destructive/20")}>
                <Text variant="destructive" className="text-sm">{uploadError}</Text>
              </div>
            )}
          </div>

          {/* Conditional: Only show "Start Fresh" if enabled */}
          {showStartFresh && (
            <>
              <Separator />
              <div>
                <div className={cn("flex items-center gap-3 mb-4")}>
                  <div className={cn("w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center")}>
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <Heading level="h3" className="text-lg font-semibold">Create New Resume</Heading>
                  </div>
                </div>
                <Button onClick={onCreateNew} className="w-full" size="lg">
                  Start from Scratch
                </Button>
              </div>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeUploadModal;
